import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import SoftButton from './SoftButton';
import { playUiClick } from './humanityUiSounds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EXHIBITION_SLUG = 'this-is-humanity';

// Pixel spacing between successive series numbers along the vertical spine.
// 320 is roughly 1.4× the 224px card height — generous air for the eye.
const V_SPACING = 320;

// Lateral sway radius (px) — the spine breathes left/right via sine so the
// memory feels like a path through space, not a column.
const H_SWAY = 220;

// How far past the current camera Y we keep mounting images. Beyond this,
// items render as quiet placeholders so memory stays bounded.
const VIS_AHEAD = 950;
const VIS_BEHIND = 950;
// Horizontal pan range — on mobile the visitor can drift left/right to
// "look around the entity's brain", but we clamp so they can't pan into
// empty void forever.
const H_PAN_LIMIT = 380;

// Wave propagation speed for the click impulse (px / second). Faster than
// physical distance — the impulse should feel like memory activation, not a
// shock wave.
const IMPULSE_SPEED = 1700;

/**
 * Deterministic 0..1 hash so jitter and depth are stable per series number.
 */
function rng(seed) {
  const x = Math.sin(seed * 9999 + 4321.5) * 10000;
  return x - Math.floor(x);
}

/**
 * Sort and lay out items along a vertical "memory spine".
 *  i=0 sits at y=0 (oldest series_number we have)
 *  i=n-1 sits at y=(n-1)*V_SPACING (newest)
 * Each item gets a sine-driven lateral offset and a slight depth variance.
 */
function layoutSpine(items) {
  // Oldest first so the visitor scrolls down through the entity's history.
  const sorted = [...items].sort((a, b) => {
    const an = a.series_number ?? 0;
    const bn = b.series_number ?? 0;
    return an - bn;
  });
  return sorted.map((item, i) => {
    const seed = (item.series_number ?? i) + 1;
    const xNorm = Math.sin(i * 0.85 + rng(seed * 3) * 0.6);
    const x = xNorm * H_SWAY;
    const y = i * V_SPACING;
    const depth = 0.78 + 0.22 * Math.sin(i * 1.3 + rng(seed * 7));
    const drift = 0.04 + rng(seed * 11) * 0.04;
    const phase = rng(seed * 13) * Math.PI * 2;
    return { item, i, x, y, depth, drift, phase };
  });
}

/**
 * EntityGallery — vertical memory spine.
 *
 *  - items are sorted by series_number ascending; the spine extends downward
 *    so the visitor scrolls DOWN through the entity's history
 *  - the camera (cameraYRef) is smoothly eased toward a target driven by
 *    wheel deltas and touch drag
 *  - only items within ±950 px of the camera get a live <img>; far cards
 *    render as quiet series-only placeholders so memory stays bounded
 *  - a single rAF loop drives:
 *       camera easing, custom cursor easing, click-impulse propagation
 *  - click impulse travels along the spine in time (IMPULSE_SPEED px/s) —
 *    nearby cards brighten in sequence, connection segments pulse white
 *  - background AmbientCanvas (mode="gallery") draws the code rain + cursor
 *    wave underneath; this overlay is transparent so the canvas shines
 *    through
 */
export default function EntityGallery({ onReturn }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [focused, setFocused] = useState(null);
  // visTick rises every 200 ms so React recomputes which cards mount images.
  const [visTick, setVisTick] = useState(0);

  // Camera + cursor state (refs only — no React state churn).
  // Camera tracks both X (free pan) and Y (memory spine) so mobile drag
  // feels like moving through the entity's brain, not scrolling a page.
  const cameraRef = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const cursorRef = useRef({ x: 0, y: 0, tx: 0, ty: 0, over: false, pulseT: -10 });
  const impulseRef = useRef({ active: false, sourceY: 0, t0: 0 });
  const rafRef = useRef(0);
  const visTimerRef = useRef(0);
  const sizeRef = useRef({ w: 1280, h: 720 });
  const dragRef = useRef({ active: false, lastX: 0, lastY: 0, moved: 0 });

  const fieldRef = useRef(null);
  const cursorEl = useRef(null);

  // ── fetch items ────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/humanity/gallery`, {
          params: { exhibition_slug: EXHIBITION_SLUG, limit: 100 },
          timeout: 12000,
        });
        if (cancelled) return;
        setItems(Array.isArray(r.data?.items) ? r.data.items : []);
      } catch (err) {
        console.warn('[HumanityMirror] gallery fetch failed', err?.message || err);
        if (!cancelled) setError('memory_unavailable');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const placed = useMemo(
    () => (Array.isArray(items) ? layoutSpine(items) : []),
    [items],
  );
  const totalY = placed.length ? (placed.length - 1) * V_SPACING : 0;

  // ── input + animation loop ─────────────────────────────────
  useEffect(() => {
    if (!placed.length) return undefined;
    const onResize = () => { sizeRef.current = { w: window.innerWidth, h: window.innerHeight }; };
    onResize();

    const clampTarget = () => {
      const cam = cameraRef.current;
      // Allow a half-viewport overscroll at each end so the first / last
      // memory feels like floating in space, not pinned to an edge.
      const minY = -sizeRef.current.h * 0.25;
      const maxY = totalY + sizeRef.current.h * 0.25;
      if (cam.ty < minY) cam.ty = minY;
      if (cam.ty > maxY) cam.ty = maxY;
      if (cam.tx < -H_PAN_LIMIT) cam.tx = -H_PAN_LIMIT;
      if (cam.tx > H_PAN_LIMIT) cam.tx = H_PAN_LIMIT;
    };

    const onWheel = (e) => {
      // 1.5 multiplier gives wheel a confident, gallery-walk pace without
      // feeling lurching. Easing (0.16) softens overscroll spikes.
      cameraRef.current.ty += e.deltaY * 1.5;
      // shift+wheel pans horizontally on desktop so curious visitors can
      // drift sideways without a touchpad.
      if (e.shiftKey) cameraRef.current.tx += e.deltaY * 1.5;
      clampTarget();
    };

    // Two-axis drag (pointer + touch combined via Pointer Events).
    const onPointerDown = (e) => {
      // Skip clicks on cards / buttons / links so we don't steal those.
      const target = e.target;
      if (target && typeof target.closest === 'function') {
        if (target.closest('button, a, [data-clickable="true"]')) return;
      }
      dragRef.current.active = true;
      dragRef.current.lastX = e.clientX;
      dragRef.current.lastY = e.clientY;
      dragRef.current.moved = 0;
    };
    const onPointerMove = (e) => {
      cursorRef.current.tx = e.clientX;
      cursorRef.current.ty = e.clientY;
      const d = dragRef.current;
      if (!d.active) return;
      const dx = e.clientX - d.lastX;
      const dy = e.clientY - d.lastY;
      d.lastX = e.clientX;
      d.lastY = e.clientY;
      d.moved += Math.abs(dx) + Math.abs(dy);
      // Drag DOWN should pull older memories DOWN with the finger → reveal
      // newer memories. So camera.ty INCREASES by -dy.
      cameraRef.current.ty -= dy * 1.6;
      cameraRef.current.tx -= dx * 1.4;
      clampTarget();
    };
    const onPointerUp = () => {
      dragRef.current.active = false;
    };

    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerup', onPointerUp, { passive: true });
    window.addEventListener('pointercancel', onPointerUp, { passive: true });

    const tick = () => {
      // Camera easing — fast enough to explore many memories quickly, but
      // soft enough to feel like soft tissue rather than a stiff scroll.
      const cam = cameraRef.current;
      cam.y += (cam.ty - cam.y) * 0.13;
      cam.x += (cam.tx - cam.x) * 0.13;
      // Custom cursor — slightly slower easing so it trails the pointer.
      const cur = cursorRef.current;
      cur.x += (cur.tx - cur.x) * 0.30;
      cur.y += (cur.ty - cur.y) * 0.30;

      const fld = fieldRef.current;
      if (fld) {
        // Field is anchored to viewport centre then translated by -camera.
        // Positive cam.x/y means the visitor has dragged forward → field
        // translates inverse so the memories scroll past correctly.
        fld.style.transform = `translate3d(${(-cam.x).toFixed(1)}px, ${(-cam.y).toFixed(1)}px, 0)`;
      }
      if (cursorEl.current) {
        const c = cursorEl.current;
        const pulseAge = (performance.now() / 1000) - cur.pulseT;
        const pulseAmp = pulseAge < 0.6 ? Math.max(0, 1 - pulseAge / 0.6) : 0;
        const scale = (cur.over ? 1.8 : 1) + pulseAmp * 1.6;
        c.style.transform = `translate3d(${cur.x.toFixed(1)}px, ${cur.y.toFixed(1)}px, 0) translate(-50%, -50%) scale(${scale.toFixed(3)})`;
        c.style.opacity = (cur.over ? 1 : 0.78).toFixed(2);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    visTimerRef.current = setInterval(() => setVisTick((n) => (n + 1) & 0xffff), 200);

    return () => {
      cancelAnimationFrame(rafRef.current);
      if (visTimerRef.current) clearInterval(visTimerRef.current);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [placed.length, totalY]);

  // ── proximity image-mount window ────────────────────────────
  const visibleIds = useMemo(() => {
    void visTick;
    if (!placed.length) return new Set();
    const camY = cameraRef.current.y;
    const minY = camY - VIS_BEHIND;
    const maxY = camY + VIS_AHEAD;
    const out = new Set();
    for (const p of placed) {
      if (p.y >= minY && p.y <= maxY) out.add(p.item.id);
    }
    if (out.size < 4) {
      // Always include a small window so the first frame isn't empty.
      for (let i = 0; i < Math.min(4, placed.length); i += 1) {
        out.add(placed[i].item.id);
      }
    }
    return out;
  }, [placed, visTick]);

  // ── click handler — opens focus + emits impulse ─────────────
  const handleCardClick = (p) => {
    if (dragRef.current.moved > 8) {
      dragRef.current.moved = 0;
      return;
    }
    playUiClick();
    impulseRef.current = { active: true, sourceY: p.y, t0: performance.now() / 1000 };
    cursorRef.current.pulseT = performance.now() / 1000;
    setTimeout(() => setFocused(p.item), 220);
  };
  const handleCardHover = (over) => { cursorRef.current.over = over; };

  // ── render states ───────────────────────────────────────────
  if (error) {
    return (
      <GalleryShell onReturn={onReturn}>
        <div className="text-center space-y-3" data-testid="gallery-error">
          <p className="font-mono tracking-[0.28em] uppercase text-[11px] text-rose-300/75">
            The memory could not open.
          </p>
          <SoftButton variant="ghost" size="sm" onClick={() => { playUiClick(); onReturn?.(); }} testId="gallery-return-error">
            Return to entity
          </SoftButton>
        </div>
      </GalleryShell>
    );
  }
  if (items === null) {
    return (
      <GalleryShell onReturn={onReturn}>
        <p className="font-mono tracking-[0.32em] uppercase text-[11px] text-white/55" data-testid="gallery-loading">
          Opening the entity memory.
        </p>
      </GalleryShell>
    );
  }
  if (!items.length) {
    return (
      <GalleryShell onReturn={onReturn}>
        <div className="text-center space-y-3" data-testid="gallery-empty">
          <p className="font-mono tracking-[0.28em] uppercase text-[11px] text-white/65">
            No images have formed yet.
          </p>
          <p className="font-mono tracking-[0.22em] uppercase text-[10px] text-white/40">
            The entity is still waiting for its first memory.
          </p>
        </div>
      </GalleryShell>
    );
  }

  return (
    <GalleryShell onReturn={onReturn}>
      <div className="absolute inset-0 overflow-hidden">
        {/* Vertical field — anchored at viewport centre then translated by
            -cameraY so larger Y values are below the centre. */}
        <div
          className="absolute left-1/2 top-1/2 will-change-transform"
          ref={fieldRef}
          style={{ transform: 'translate3d(0, 0, 0)' }}
        >
          <SpineConnections placed={placed} impulseRef={impulseRef} cursorRef={cursorRef} cameraRef={cameraRef} sizeRef={sizeRef} />
          {placed.map((p) => (
            <FloatingCard
              key={p.item.id}
              placed={p}
              visible={visibleIds.has(p.item.id)}
              onClick={() => handleCardClick(p)}
              onHover={handleCardHover}
              impulseRef={impulseRef}
              cursorRef={cursorRef}
              cameraRef={cameraRef}
              sizeRef={sizeRef}
            />
          ))}
        </div>
      </div>

      {/* Custom cursor — additive halo, doesn't replace native cursor */}
      <div
        ref={cursorEl}
        className="humanity-gallery-cursor pointer-events-none fixed top-0 left-0 z-[60]"
        aria-hidden
      />

      <AnimatePresence>
        {focused && (
          <FocusOverlay item={focused} onClose={() => setFocused(null)} />
        )}
      </AnimatePresence>
    </GalleryShell>
  );
}

// ─── Shell ──────────────────────────────────────────────────────

function GalleryShell({ children, onReturn }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.9 }}
      className="fixed inset-0 z-20 overflow-hidden"
      // Transparent — the underlying AmbientCanvas (mode='gallery') draws
      // the code rain + cursor wave + click ripples behind everything.
      // touchAction: none ensures mobile drag doesn't trigger native
      // page scroll / pull-to-refresh while the visitor explores the
      // memory field with one finger.
      style={{ background: 'transparent', touchAction: 'none' }}
      data-testid="entity-gallery"
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-32 z-20"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)' }}
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-24 z-20"
        style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)' }}
      />

      <header className="absolute top-6 left-1/2 -translate-x-1/2 z-30 text-center pointer-events-none">
        <p className="font-mono tracking-[0.38em] uppercase text-[10px] sm:text-[11px] text-white/70">
          The Entity&rsquo;s Memory
        </p>
        <p className="mt-1.5 font-mono tracking-[0.22em] uppercase text-[9px] sm:text-[10px] text-white/45">
          Fragments created from previous conversations.
        </p>
      </header>

      <div className="absolute top-5 left-5 sm:top-6 sm:left-6 z-30">
        <SoftButton variant="ghost" size="sm" onClick={() => { playUiClick(); onReturn?.(); }} testId="gallery-return">
          ← Return to entity
        </SoftButton>
      </div>

      <div className="absolute inset-0 flex items-center justify-center">
        {children}
      </div>

      <footer className="absolute bottom-5 left-0 right-0 z-30 pointer-events-none text-center">
        <p className="font-mono tracking-[0.30em] uppercase text-[9px] sm:text-[10px] text-white/40">
          Scroll · click a fragment
        </p>
      </footer>
    </motion.div>
  );
}

// ─── Spine connection layer (SVG) ──────────────────────────────

function SpineConnections({ placed, impulseRef, cursorRef, cameraRef, sizeRef }) {
  const svgRef = useRef(null);

  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const svg = svgRef.current;
      if (svg) {
        const now = performance.now() / 1000;
        const imp = impulseRef.current;
        const age = imp.active ? now - imp.t0 : -1;

        // Cursor position in FIELD coordinates so we can check proximity to
        // each strand's midpoint without a layout read per frame.
        let cursorFieldX = 1e9;
        let cursorFieldY = 1e9;
        if (cursorRef && cameraRef && sizeRef) {
          const size = sizeRef.current;
          const cam = cameraRef.current;
          const cur = cursorRef.current;
          cursorFieldX = cur.x - size.w / 2 + cam.x;
          cursorFieldY = cur.y - size.h / 2 + cam.y;
        }

        const paths = svg.querySelectorAll('path[data-y0]');
        for (let i = 0; i < paths.length; i += 1) {
          const path = paths[i];
          const y0 = Number(path.dataset.y0);
          const y1 = Number(path.dataset.y1);
          const mx = Number(path.dataset.mx);
          const baseAlpha = Number(path.dataset.base) || 0.14;
          let alpha = baseAlpha;
          // 1) impulse propagation along the spine
          if (age >= 0) {
            const midY = (y0 + y1) / 2;
            const dist = Math.abs(midY - imp.sourceY);
            const arrive = dist / IMPULSE_SPEED;
            const since = age - arrive;
            if (since > 0 && since < 0.9) {
              const env = Math.max(0, 1 - since / 0.9);
              alpha = baseAlpha + env * 0.72;
            }
          }
          // 2) gentle breath via sin so strands feel alive even at rest
          const breath = 0.5 + 0.5 * Math.sin(now * 0.55 + i * 0.27);
          alpha += breath * 0.05;
          // 3) cursor proximity — strand midpoint within 220px of cursor
          //    in field coords brightens softly (max +0.38)
          const midX = (Number(path.dataset.x0) + Number(path.dataset.x1)) / 2 + mx * 0;
          const midY2 = (y0 + y1) / 2;
          const dCursor = Math.hypot(midX - cursorFieldX, midY2 - cursorFieldY);
          if (dCursor < 240) {
            alpha += (1 - dCursor / 240) * 0.38;
          }
          path.setAttribute('stroke', `rgba(190,232,238,${Math.min(0.95, alpha).toFixed(3)})`);
        }
        if (imp.active && now - imp.t0 > 2.5) imp.active = false;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [impulseRef, cursorRef, cameraRef, sizeRef]);

  // ── strand generation ──────────────────────────────────────
  // Each "connection" between two cards becomes a small bundle of 2–4
  // organic strands. Each strand has a deterministic perpendicular offset
  // + a small cubic-bezier wobble so it reads like mycelium, not a copy of
  // the same curve repeated. Counts are kept tight so total path count
  // stays under ~300 even with 50 cards.
  if (placed.length < 2) return null;
  const segments = [];
  for (let i = 0; i < placed.length - 1; i += 1) {
    segments.push({ a: placed[i], b: placed[i + 1], kind: 'spine', strands: 3 });
    if (i + 2 < placed.length && i % 3 === 0) {
      segments.push({ a: placed[i], b: placed[i + 2], kind: 'skip', strands: 2 });
    }
  }

  // SVG must be large enough to span the whole spine vertically.
  const minY = -200;
  const maxY = (placed.length - 1) * V_SPACING + 200;
  const width = 1400;

  const strandPaths = [];
  let pathKey = 0;
  for (const s of segments) {
    const ax = s.a.x;
    const ay = s.a.y;
    const bx = s.b.x;
    const by = s.b.y;
    const dx = bx - ax;
    const dy = by - ay;
    const len = Math.hypot(dx, dy) || 1;
    // Perpendicular unit vector to displace strands away from the spine.
    const px = -dy / len;
    const py = dx / len;

    for (let k = 0; k < s.strands; k += 1) {
      // Deterministic per-strand offsets seeded by the segment + strand index
      // so the network looks the same on every load but each strand is
      // distinct. Spread is wider for skip connections so they read as
      // secondary, more diffuse pathways.
      const seedK = (s.a.i + 1) * 17 + k * 13 + (s.kind === 'skip' ? 7 : 0);
      const spread = s.kind === 'skip' ? 28 : 18;
      // Center the bundle by offset (k - (n-1)/2).
      const lateral = (k - (s.strands - 1) / 2) * spread * (0.7 + rng(seedK * 3) * 0.6);
      const wob1 = (rng(seedK * 5) - 0.5) * (s.kind === 'skip' ? 24 : 14);
      const wob2 = (rng(seedK * 7) - 0.5) * (s.kind === 'skip' ? 24 : 14);
      // Two cubic-bezier control points along the segment so each strand
      // can bow gently and slightly differently from its neighbours.
      const t1 = 0.32 + rng(seedK * 11) * 0.10;
      const t2 = 0.68 - rng(seedK * 13) * 0.10;
      const c1x = ax + dx * t1 + px * (lateral + wob1);
      const c1y = ay + dy * t1 + py * (lateral + wob1) * 0.2;
      const c2x = ax + dx * t2 + px * (lateral + wob2);
      const c2y = ay + dy * t2 + py * (lateral + wob2) * 0.2;
      const baseAlpha = s.kind === 'skip' ? 0.08 : 0.12 - k * 0.015;
      const strokeWidth = s.kind === 'skip' ? 0.45 : 0.6 - k * 0.06;
      strandPaths.push(
        <path
          key={`s${pathKey++}`}
          d={`M ${ax.toFixed(1)} ${ay.toFixed(1)} C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${bx.toFixed(1)} ${by.toFixed(1)}`}
          stroke={`rgba(190,232,238,${baseAlpha.toFixed(3)})`}
          strokeWidth={Math.max(0.25, strokeWidth).toFixed(2)}
          strokeLinecap="round"
          fill="none"
          data-y0={ay}
          data-y1={by}
          data-x0={ax}
          data-x1={bx}
          data-mx={(c1x + c2x) / 2}
          data-base={baseAlpha}
        />,
      );
    }
  }

  return (
    <svg
      ref={svgRef}
      width={width}
      height={maxY - minY}
      viewBox={`${-width / 2} ${minY} ${width} ${maxY - minY}`}
      style={{ position: 'absolute', left: -width / 2, top: minY, pointerEvents: 'none' }}
      aria-hidden
    >
      {strandPaths}
    </svg>
  );
}

// ─── Floating card ─────────────────────────────────────────────

const CARD_W = 192;
const CARD_H = 256;

function FloatingCard({ placed, visible, onClick, onHover, impulseRef, cursorRef, cameraRef, sizeRef }) {
  const { item, i, x, y, depth, drift, phase } = placed;
  const ref = useRef(null);
  const glowRef = useRef(null);
  const innerRef = useRef(null);
  // hoverRef.t eases between 0 and 1 in the rAF loop. Updated in
  // onPointerEnter/Leave so the card breathes in/out smoothly rather than
  // snapping.
  const hoverRef = useRef({ target: 0, t: 0 });

  useEffect(() => {
    let raf = 0;
    const t0 = performance.now();
    const tick = (now) => {
      const dt = (now - t0) / 1000;
      // ── ambient sway ──────────────────────────────────────
      const dx = Math.cos(dt * 0.18 + phase) * drift * 30;
      const dy = Math.sin(dt * 0.13 + phase * 1.3) * drift * 22;

      // ── hover easing ──────────────────────────────────────
      const h = hoverRef.current;
      h.t += (h.target - h.t) * 0.12;
      const hov = h.t; // 0..1

      // ── pointer parallax inside hover radius ─────────────
      // Compute cursor in field coords once; then pointer-relative-to-card.
      let pdx = 0; let pdy = 0;
      if (hov > 0.01 && cursorRef && cameraRef && sizeRef) {
        const size = sizeRef.current;
        const cam = cameraRef.current;
        const cur = cursorRef.current;
        const cursorFx = cur.x - size.w / 2 + cam.x;
        const cursorFy = cur.y - size.h / 2 + cam.y;
        // Position relative to this card's field centre, clamped to ±1.
        const cardCx = x + dx;
        const cardCy = y + dy;
        const rx = (cursorFx - cardCx) / (CARD_W / 2);
        const ry = (cursorFy - cardCy) / (CARD_H / 2);
        // Card parallax-drifts up to 14 px toward the cursor, eased by hover.
        pdx = Math.max(-1, Math.min(1, rx)) * 14 * hov;
        pdy = Math.max(-1, Math.min(1, ry)) * 11 * hov;
      }

      const finalX = x + dx + pdx;
      const finalY = y + dy + pdy;
      // Slight scale + lift while hovered so the card reads as "the entity
      // sensed your presence". Lift is part of scale (1 + 0.04).
      const scaleFactor = depth * (1 + hov * 0.04);

      const el = ref.current;
      if (el) {
        el.style.transform = `translate3d(${finalX.toFixed(1)}px, ${finalY.toFixed(1)}px, 0) scale(${scaleFactor.toFixed(3)})`;
      }
      // Subtle tilt on the inner frame using rotateX/Y so the card feels
      // alive but never breaks the gallery's flat composition.
      const inner = innerRef.current;
      if (inner) {
        const rotY = hov * (pdx / 14) * 4.5;
        const rotX = hov * -(pdy / 11) * 4.5;
        inner.style.transform = `perspective(800px) rotateX(${rotX.toFixed(2)}deg) rotateY(${rotY.toFixed(2)}deg)`;
        inner.style.boxShadow = hov > 0.02
          ? `0 0 ${(28 + hov * 36).toFixed(0)}px rgba(190,232,238,${(0.10 + hov * 0.22).toFixed(2)}),`
            + ` inset 0 0 ${(14 + hov * 16).toFixed(0)}px rgba(255,255,255,${(0.02 + hov * 0.05).toFixed(2)})`
          : '0 0 24px rgba(144, 224, 239, 0.07), inset 0 0 12px rgba(255,255,255,0.02)';
        inner.style.borderColor = hov > 0.02
          ? `rgba(190,232,238,${(0.18 + hov * 0.30).toFixed(2)})`
          : 'rgba(255,255,255,0.12)';
      }

      // ── impulse glow ─────────────────────────────────────
      const imp = impulseRef.current;
      const glow = glowRef.current;
      if (glow) {
        let opacity = 0;
        if (imp.active) {
          const age = (now / 1000) - imp.t0;
          const dist = Math.abs(y - imp.sourceY);
          const arrive = dist / IMPULSE_SPEED;
          const since = age - arrive;
          if (since > 0 && since < 0.85) {
            const env = Math.max(0, 1 - since / 0.85);
            opacity = env * 0.85;
          }
        }
        glow.style.opacity = opacity.toFixed(3);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [x, y, depth, drift, phase, impulseRef, cursorRef, cameraRef, sizeRef]);

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      onPointerEnter={() => { onHover(true); hoverRef.current.target = 1; }}
      onPointerLeave={() => { onHover(false); hoverRef.current.target = 0; }}
      data-testid={`gallery-card-${item.series_number ?? i}`}
      data-clickable="true"
      className="absolute will-change-transform"
      style={{
        left: -CARD_W / 2,
        top: -CARD_H / 2,
        width: `${CARD_W}px`,
        height: `${CARD_H}px`,
        transformOrigin: 'center center',
        opacity: 0.55 + depth * 0.4,
      }}
    >
      <div
        ref={innerRef}
        className="relative w-full h-full rounded-[10px] overflow-hidden border bg-black/55"
        style={{
          borderColor: 'rgba(255,255,255,0.12)',
          boxShadow: '0 0 24px rgba(144, 224, 239, 0.07), inset 0 0 12px rgba(255,255,255,0.02)',
          transition: 'box-shadow 300ms ease, border-color 300ms ease, transform 0ms',
          transformStyle: 'preserve-3d',
        }}
      >
        {/* Impulse glow ring */}
        <div
          ref={glowRef}
          className="absolute inset-0 rounded-[10px] pointer-events-none"
          style={{
            opacity: 0,
            boxShadow: '0 0 30px rgba(190,232,238,0.7), inset 0 0 22px rgba(190,232,238,0.35)',
            transition: 'opacity 120ms linear',
          }}
        />

        {visible ? (
          <img
            src={item.image_url}
            alt={item.title || `Humanity ${item.formatted_series || ''}`}
            loading="lazy"
            decoding="async"
            width={CARD_W}
            height={CARD_H}
            className="w-full h-full block opacity-90 transition-opacity duration-500 hover:opacity-100"
            style={{ objectFit: 'cover' }}
          />
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ background: 'radial-gradient(ellipse at center, rgba(144,224,239,0.06) 0%, rgba(0,0,0,0.85) 70%)' }}
          >
            <span className="font-mono tracking-[0.30em] uppercase text-[10px] text-white/35" aria-hidden>
              {item.formatted_series || ''}
            </span>
          </div>
        )}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3"
          style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.65) 100%)' }}
        />
        {visible && (
          <p
            className="absolute bottom-1.5 left-2 right-2 font-mono tracking-[0.22em] uppercase text-[9px] text-white/70 text-center truncate"
            style={{ textShadow: '0 0 6px rgba(0,0,0,0.85)' }}
          >
            {item.formatted_series || ''}
          </p>
        )}
      </div>
    </button>
  );
}

// ─── Focus mode (camera-forward open) ─────────────────────────

// Above this character count, the entity thought needs READ MORE.
const THOUGHT_COLLAPSED_MAX = 170;

function FocusOverlay({ item, onClose }) {
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const thought = item.entity_thought
    || 'Humanity appears here as a fragment left by a conversation.';
  const needsTruncate = thought.length > THOUGHT_COLLAPSED_MAX;

  // Spring + delayed children give the focus open a cinematic, gentle feel:
  // image scales in with a low-stiffness spring (~0.9s), metadata fades in
  // ~0.35s later so the visitor sees the artwork first, then the words.
  const containerSpring = { type: 'spring', stiffness: 60, damping: 24, mass: 1 };
  const imageVariants = {
    initial: { scale: 0.78, opacity: 0, filter: 'blur(14px)' },
    animate: { scale: 1, opacity: 1, filter: 'blur(0px)' },
    exit: { scale: 0.9, opacity: 0, filter: 'blur(8px)' },
  };
  const metaVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 6 },
  };

  return (
    <motion.div
      initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
      animate={{ opacity: 1, backdropFilter: 'blur(6px)' }}
      exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
      transition={{ duration: 0.95, ease: [0.22, 0.9, 0.3, 1] }}
      className="fixed inset-0 z-40 bg-black/85 flex items-center justify-center px-5 py-8 overflow-y-auto"
      onClick={onClose}
      data-testid="gallery-focus"
    >
      <motion.div
        className="relative max-w-3xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col md:flex-row md:items-center md:gap-10 gap-6 text-center md:text-left">
          {/* Image — slow cinematic open */}
          <motion.div
            variants={imageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={containerSpring}
            className="relative w-full md:w-[58%] mx-auto rounded-[14px] overflow-hidden border border-white/10 bg-black"
            style={{
              aspectRatio: '3 / 4',
              boxShadow: '0 0 70px rgba(144, 224, 239, 0.15), inset 0 0 22px rgba(255,255,255,0.02)',
            }}
          >
            <img
              src={item.image_url}
              alt={item.title || 'Humanity fragment'}
              className="w-full h-full block"
              style={{ objectFit: 'cover' }}
              data-testid="focus-image"
            />
          </motion.div>

          {/* Metadata + thought — fades in after the image lands */}
          <motion.div
            variants={metaVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 0.9, 0.3, 1] }}
            className="md:flex-1 md:min-w-0 space-y-3"
          >
            <p
              className="font-mono tracking-[0.38em] uppercase text-[10px] sm:text-[11px] text-white/55"
              data-testid="focus-series"
            >
              This Is Humanity {item.formatted_series || ''}
            </p>
            {item.title && (
              <h2
                className="humanity-result-title font-mono uppercase tracking-[0.22em] text-[14px] sm:text-[16px] text-white leading-snug"
                data-testid="focus-title"
              >
                {item.title}
              </h2>
            )}
            {item.ticket_caption && (
              <p
                className="font-mono italic text-[11px] sm:text-[12px] text-white/55 leading-relaxed"
                data-testid="focus-caption"
              >
                &ldquo;{item.ticket_caption}&rdquo;
              </p>
            )}
            <div
              className="pt-4 mt-1 border-t border-white/10 space-y-2"
              data-testid="focus-entity-thought-wrap"
            >
              <p
                className="font-mono tracking-[0.36em] uppercase text-[9px] sm:text-[10px] text-cyan-200/65"
                data-testid="focus-entity-thought-label"
              >
                Entity Thought
              </p>
              <p
                className={`font-mono tracking-[0.02em] text-[12px] sm:text-[13px] text-white/75 leading-[1.65] ${
                  needsTruncate && !expanded ? 'humanity-thought-clamp' : ''
                }`}
                data-testid="focus-entity-thought"
              >
                {thought}
              </p>
              {needsTruncate && (
                <button
                  type="button"
                  onClick={() => { playUiClick(); setExpanded((v) => !v); }}
                  data-testid="focus-entity-thought-toggle"
                  data-clickable="true"
                  className="font-mono tracking-[0.30em] uppercase text-[9px] sm:text-[10px] text-cyan-200/70 hover:text-cyan-100/95 underline underline-offset-[3px] decoration-cyan-200/30 hover:decoration-cyan-100/70 transition-colors"
                >
                  {expanded ? 'Show less' : 'Read more'}
                </button>
              )}
            </div>
            <div className="pt-3 flex items-center md:justify-start justify-center gap-3">
              <SoftButton variant="ghost" size="sm" onClick={() => { playUiClick(); onClose?.(); }} testId="focus-close">
                Return to entity
              </SoftButton>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}
